// @ts-nocheck

import * as React from 'react';
import clamp from 'lodash/clamp';
import pick from 'lodash/pick';

import SliderHandle from 'src/images/slider-handle-ring.svg';

import {classify} from 'src/utils/classify';

import css from './sliderV2.css';


type Label = {
  value: string;
  label: React.ReactNode;
  percentage: number;
  unit: string;
};

type Props = {
  className?: string;
  min: number;
  max: number;
  start?: number;
  end?: number;
  defaultStart?: number;
  defaultEnd?: number;
  disabled: boolean;
  step: number;
  title?: string;
  valueFormatter: (value: number) => string;
  unit: string;
  onChange?: (values: {start: number; end: number}) => void;
  step?: number;
  showLabels?: boolean;
  labelFormatter: (labelObj: Label) => labelObj & {
    label: string;
  };
  labelFilter: (arg1: Label) => boolean;
  fixedStart?: boolean;
  classNames?: {
    labelsContainer: string | null | undefined;
    label: string | null | undefined;
  };
};

type State = {
  startValue?: number;
  endValue?: number;
  start: number;
  end: number;
  movingStart: boolean;
  movingEnd: boolean;
};

//append unit to labels
const defaultLabelFormatter = (label: unknown): Label => ({
  ...label,
  label: `${label.value} ${label.unit}`,
});
//show all labels
const defaultLabelFilter = (_: unknown) => true;

// NOTE(elliot): 'start' and 'end' are arbitrary while moving.
export class Slider extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      start: props.start || props.defaultStart || props.min,
      end: props.end || props.defaultEnd || props.max,
      movingStart: false,
      movingEnd: false,

      // @NOTE(elliot): Initial props to compare so this component can be controlled by props.
      startValue: props.start,
      endValue: props.end,
    };
  }

  static defaultProps = {
    valueFormatter: String,
    unit: '',
    showLabels: false,
    fixedStart: false,
  };

  static getDerivedStateFromProps(nextProps: Props, prevState: State) {
    if (
      (nextProps.start !== undefined &&
        nextProps.start !== prevState.startValue) ||
      (nextProps.end !== undefined && nextProps.end !== prevState.endValue)
    ) {
      return {
        start: nextProps.start,
        end: nextProps.end,
        startValue: nextProps.start,
        endValue: nextProps.end,
      };
    }
    return null;
  }

  componentWillUnmount() {
    this.removeMouseMove();
    this.removeMouseUp();
  }

  node: React.ReactElement<any>;
  trackNode: React.ReactElement<any>;

  handleMouseDown = (type: 'start' | 'end') => () => {
    const stateKey = type === 'start' ? 'movingStart' : 'movingEnd';
    this.setState({[stateKey]: true}, () => {
      this.addMouseMove();
      this.addMouseUp();
    });
  };

  handleStartMouseDown = this.handleMouseDown('start');
  handleEndMouseDown = this.handleMouseDown('end');

  getTrackClientRect = (): ClientRect => this.trackNode.getClientRect();

  handleMouseMove = (e: React.MouseEvent<any>) => {
    if (this.state.movingStart || this.state.movingEnd) {
      const clientRect = this.getTrackClientRect();
      const value = getValueFromEvent(
        e,
        clientRect,
        this.props.min,
        this.props.max,
      );
      const stateKey = this.state.movingStart ? 'start' : 'end';
      this.setState({[stateKey]: value});
    }
  };

  addMouseMove = () => {
    this.removeMouseMove();
    this.node.ownerDocument.addEventListener('mousemove', this.handleMouseMove);
  };

  removeMouseMove = () => {
    this.node.ownerDocument.removeEventListener(
      'mousemove',
      this.handleMouseMove,
    );
  };

  handleMouseUp = (e: React.MouseEvent<any>) => {
    if (this.state.movingStart || this.state.movingEnd) {
      const clientRect = this.getTrackClientRect();
      const value = getValueFromEvent(
        e,
        clientRect,
        this.props.min,
        this.props.max,
        this.props.step,
      );
      const movingKey = this.state.movingStart ? 'movingStart' : 'movingEnd';
      const valueKey = this.state.movingStart ? 'start' : 'end';
      requestAnimationFrame(() => {
        this.setState({[movingKey]: false, [valueKey]: value}, () => {
          this.props.onChange(pick(this.state, ['start', 'end']));
        });
      });
    }
  };

  addMouseUp = () => {
    this.removeMouseUp();
    this.node.ownerDocument.addEventListener('mouseup', this.handleMouseUp);
  };

  removeMouseUp = () => {
    this.node.ownerDocument.removeEventListener('mouseup', this.handleMouseUp);
  };

  handleTrackMouseDown = (e: React.MouseEvent<any>) => {
    if (this.props.disabled) {
      return;
    }
    e.preventDefault();
    const clientRect = this.getTrackClientRect();
    const value = getValueFromEvent(
      e,
      clientRect,
      this.props.min,
      this.props.max,
    );
    //if fixed start, only move the end handle, start handle is fixed
    if (this.props.fixedStart) {
      const stateKey = 'movingEnd';
      this.setState({[stateKey]: true, end: value}, () => {
        this.addMouseMove();
        this.addMouseUp();
      });
    } else {
      const startDifference = Math.abs(this.state.start - value);
      const endDifference = Math.abs(this.state.end - value);
      if (startDifference < endDifference) {
        this.setState({start: value});
      } else {
        this.setState({end: value});
      }
    }
  };

  getStepLabels = (
    min: unknown,
    max: unknown,
    labelFormatOptions: unknown,
  ): Label[] => {
    const {step, unit, labelFormatter, labelFilter} = labelFormatOptions;

    let labels: unknown = [];
    for (let i = min; i <= max; i += step) {
      labels = [
        ...labels,
        {unit, value: i, percentage: getPercentageFromValue(i, min, max) * 100},
      ];
    }
    if (labels[labels.length - 1].value !== max) {
      labels = [...labels, {unit, value: max, percentage: 100}];
    }
    return labels.map(labelFormatter).filter(labelFilter);
  };

  render() {
    const {
      className,
      min,
      max,
      disabled,
      title,
      valueFormatter,
      step,
      unit,
      showLabels,
      fixedStart,
      labelFormatter = defaultLabelFormatter,
      labelFilter = defaultLabelFilter,
      classNames,
    } = this.props;
    const {start, end, movingStart, movingEnd} = this.state;
    const sharedProps = {
      min,
      max,
      disabled,
    } as const;
    const values = [start, end].sort((a, b) => a - b);
    const startLabel = valueFormatter(getValue(values[0], step, min, max));
    const endLabel = valueFormatter(getValue(values[1], step, min, max));
    const labels = this.getStepLabels(min, max, {
      step,
      unit,
      labelFormatter,
      labelFilter,
    });
    return (
      <div
        className={classify(css.container, className)}
        ref={(node) => (this.node = node)}
      >
        {Boolean(title) && <div className={css.title}>{title}</div>}
        {!fixedStart && (
          <div
            className={css.selectedRange}
          >{`${startLabel} - ${endLabel}${unit}`}</div>
        )}
        <div className={css.slider}>
          <Track
            {...sharedProps}
            onMouseDown={this.handleTrackMouseDown}
            start={start}
            end={end}
            ref={(trackNode) => (this.trackNode = trackNode)}
          />
          {showLabels && <Labels labels={labels} classNames={classNames} />}
          {/* Start Handle */}
          {!fixedStart && (
            <Handle
              {...sharedProps}
              value={start}
              moving={movingStart}
              onMouseDown={this.handleStartMouseDown}
              iconClassName={css.startHandle}
            />
          )}
          {/* End Handle */}
          <Handle
            {...sharedProps}
            value={end}
            moving={movingEnd}
            onMouseDown={this.handleEndMouseDown}
            iconClassName={css.endHandle}
          />
          <input type="hidden" name="start" value={start} />
          <input type="hidden" name="end" value={end} />
        </div>
      </div>
    );
  }
}

const getPercentageFromPosition = (position: unknown, {width}) => {
  const percentage = position / width;

  return percentage || 0;
};

const getValueFromEvent = (
  e: React.MouseEvent<any>,
  clientRect: ClientRect,
  min: number,
  max: number,
  step?: number,
) => {
  const {clientX} = e;
  const {left, width} = clientRect;

  const position = clamp(clientX - left, 0, width);
  const percentage = getPercentageFromPosition(position, clientRect);
  const range = max - min;
  const value = min + range * percentage;

  return step ? getStepValue(value, step, min, max) : value;
};

const getPercentageFromValue = (value: unknown, min: unknown, max: unknown) => {
  const validValue = clamp(value, min, max);
  const range = max - min;
  const percentage = (validValue - min) / range;

  return percentage || 0;
};

const getStepValue = (
  value: number,
  step: number,
  min: number,
  max: number,
) => {
  let newValue = Math.round(value / step) * step;
  newValue = newValue < min ? min : newValue;
  newValue = newValue > max ? max : newValue;
  return newValue;
};

const getValue = (value: unknown, step: unknown, min: unknown, max: unknown) =>
  step ? getStepValue(value, step, min, max) : value;

const Handle = ({
  className,
  iconClassName,
  min,
  max,
  value,
  disabled,
  onMouseDown,
}: {
  className?: string;
  iconClassName?: string;
  min: number;
  max: number;
  value: number;
  disabled: boolean;
  onMouseDown: (e: React.MouseEvent<any>) => void;
}) => {
  const percentage = getPercentageFromValue(value, min, max) * 100;
  const style = {
    left: `${percentage}%`,
  } as const;
  return (
    <div
      className={classify(css.handle, className)}
      style={style}
      role="slider"
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value}
      aria-disabled={disabled}
      onMouseDown={onMouseDown}
    >
      <SliderHandle className={iconClassName} />
    </div>
  );
};

class Track extends React.Component<{
  start: number;
  end: number;
  min: number;
  max: number;
  step: number;
  onMouseDown: (e: React.MouseEvent<any>) => void;
}> {
  node: React.ReactElement<any>;

  getClientRect = () => (this.node ? this.node.getBoundingClientRect() : null);

  render() {
    const {min, max} = this.props;
    const [start, end] = [this.props.start, this.props.end].sort(
      (a, b) => a - b,
    );
    const startPercentage = getPercentageFromValue(start, min, max);
    const endPercentage = getPercentageFromValue(end, min, max);
    const activeStyle = {
      width: `${(endPercentage - startPercentage) * 100}%`,
      left: `${startPercentage * 100}%`,
    } as const;

    return (
      <div
        className={css.track}
        onMouseDown={this.props.onMouseDown}
        ref={(node) => (this.node = node)}
      >
        <div className={css.activeTrack} style={activeStyle} />
      </div>
    );
  }
}

export type LabelsProps = {
  labels: Label[];
  className?: {
    labelsContainer: string | null | undefined;
    label: string | null | undefined;
  };
};

const Labels = ({labels, classNames}: LabelsProps) => (
  <div className={classify(css.labelsContainer, classNames?.labelsContainer)}>
    {labels.map((label: Label) => {
      let style = {left: `${label.percentage}%`, transform: `translateX(-50%)`};

      if (label.percentage === 0) {
        style = {left: `${label.percentage}%`};
      }
      if (label.percentage === 100) {
        style = {right: `${100 - label.percentage}%`};
      }

      return (
        <label
          key={label.value}
          className={classify(css.label, classNames?.label)}
          style={style}
        >
          {label.label}
        </label>
      );
    })}
  </div>
);
